import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Text } from '@amzn/stencil-react-components/text';
import { Chip, ChipBackgroundColor, ChipSize } from '@amzn/stencil-react-components/chip';
import { IconAlertTriangleFillSmall, IconBookmarkSmall } from '@amzn/stencil-react-icons';

import type { FailureTheme } from 'src/types/starlight/AuditInsights';
import { SectionHeading } from './SectionHeading';
import { edgeLine } from './panelEdges';

/**
 * Neutral, not critical: the heading's warning glyph already says these are failures, and
 * outlining every card in red would drown the one thing on the page that is genuinely
 * urgent — an overdue task.
 */
const THEME_EDGE = edgeLine('color.status.neutral-border');

interface FailureThemesSectionProps {
  themes: FailureTheme[] | undefined;
  heading: string;
  /** Labels a question id so it reads as a reference rather than a count. */
  formatQuestionId: (questionId: string) => string;
}

/**
 * Failed questions grouped by what they have in common, each with the reading behind it.
 *
 * Renders nothing when there are no themes, which is the normal shape of an audit that
 * failed nothing.
 */
export function FailureThemesSection({
  themes,
  heading,
  formatQuestionId,
}: FailureThemesSectionProps): JSX.Element | null {
  const cards = (themes ?? []).filter(hasSomethingToSay);

  if (cards.length === 0) {
    return null;
  }

  return (
    <Col gridGap="dimensions.spacing.200">
      <SectionHeading
        icon={<IconAlertTriangleFillSmall color="color.status.critical-icon" />}
        label={heading}
      />
      {cards.map((theme) => (
        <ThemeCard
          // Keyed on what the card shows, not on the question ids alone: a theme earns its
          // card on a title or a reading, so it can arrive citing no questions, and two
          // themes can cover the same ones. Ids alone then repeat a key and React
          // reconciles one card's content onto another.
          key={themeKey(theme)}
          theme={theme}
          formatQuestionId={formatQuestionId}
        />
      ))}
    </Col>
  );
}

/** True when the theme carries a title or a reading. Question ids alone say nothing. */
function hasSomethingToSay(theme: FailureTheme): boolean {
  return !!theme.themeName || !!theme.insight;
}

/**
 * Identifies a card by the wording it shows.
 *
 * Two themes are the same card only when their title, reading and cited questions all
 * agree, at which point they draw identically anyway.
 */
function themeKey(theme: FailureTheme): string {
  return [theme.themeName, theme.insight, theme.questionIds.join('-')].join('|');
}

/**
 * One theme, as a card: the finding, then its tags, then the reading.
 *
 * Same shape as `StrengthsSection`'s strength card, so the two halves of the reading scan
 * alike and only the tone tells them apart.
 *
 * The tags sit opposite the finding rather than trailing it, so a scan down the column
 * reads the findings on the left and the references on the right.
 */
function ThemeCard({
  theme,
  formatQuestionId,
}: {
  theme: FailureTheme;
  formatQuestionId: (questionId: string) => string;
}): JSX.Element {
  return (
    <Col
      backgroundColor="color.surface.bg-muted"
      borderRadius="dimensions.border.radius.200"
      padding="dimensions.spacing.300"
      gridGap="dimensions.spacing.200"
      minWidth={0}
      css={THEME_EDGE}
    >
      <Row
        alignItems="center"
        justifyContent="space-between"
        gridGap="dimensions.spacing.200"
        flexWrap="wrap"
      >
        {theme.themeName ? (
          <Text fontWeight="bold" fontSize="T200">
            {theme.themeName}
          </Text>
        ) : null}
        {/* Questions first, then the area they sit in. The ids are what a reader follows
            up on; the path only says where. */}
        <Row alignItems="center" gridGap="dimensions.spacing.200" flexWrap="wrap">
          {theme.questionIds.map((questionId) => (
            <Chip
              key={questionId}
              size={ChipSize.ExtraSmall}
              backgroundColor={ChipBackgroundColor.Red}
            >
              {formatQuestionId(questionId)}
            </Chip>
          ))}
          {theme.processPath ? (
            <Chip icon={<IconBookmarkSmall />} size={ChipSize.ExtraSmall}>
              {theme.processPath}
            </Chip>
          ) : null}
        </Row>
      </Row>
      {theme.insight ? (
        <Text fontSize="T200" color="color.text.secondary">
          {theme.insight}
        </Text>
      ) : null}
    </Col>
  );
}
